import React, { useState } from 'react';
import {
  GitCompare,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ShieldAlert,
  MessageSquare,
  Mail,
  ExternalLink,
  ChevronRight,
  Filter,
  Check,
  Zap,
  Info
} from 'lucide-react';
import { useGst } from '../context/GstContext';
import {
  runGstr2bReconciliation,
  generateVendorNudge,
  ReconMatchResult
} from '../services/reconciliationService';
import { ExtractedGstInvoice, ReconStatus } from '../types/gst';

export const ReconciliationPage: React.FC = () => {
  const { invoices, gstr2bRecords, updateInvoice, clientLegalName, activeGstin, taxPeriod, financialYear } = useGst();

  const [activeFilter, setActiveFilter] = useState<'ALL' | 'EXACT_MATCH' | 'PARTIAL_MATCH' | 'MISSING_IN_2B' | 'BLOCKED_17_5'>('ALL');
  const [selectedNudgeItem, setSelectedNudgeItem] = useState<ReconMatchResult | null>(null);

  // Run deterministic 3-way reconciliation
  const { results, summary } = runGstr2bReconciliation(invoices, gstr2bRecords);

  const filteredResults = results.filter((res) => {
    if (activeFilter === 'ALL') return true;
    if (activeFilter === 'BLOCKED_17_5') return res.invoice.itc_eligibility === 'BLOCKED_17_5';
    return res.reconStatus === activeFilter;
  });

  const handleAcceptPortalValue = async (res: ReconMatchResult) => {
    if (!res.matched2bRecord) return;
    const portalTax = (res.matched2bRecord.integrated_tax || 0) +
      (res.matched2bRecord.central_tax || 0) +
      (res.matched2bRecord.state_tax || 0) +
      (res.matched2bRecord.cess_amount || 0);

    const updated: ExtractedGstInvoice = {
      ...res.invoice,
      total_tax: portalTax,
      recon_status: 'EXACT_MATCH'
    };

    await updateInvoice(updated, `ACCEPTED_GSTR2B_PORTAL_TAX_₹${portalTax}`);
  };

  const handleKeepBooksValue = async (res: ReconMatchResult) => {
    const updated: ExtractedGstInvoice = {
      ...res.invoice,
      recon_status: 'EXACT_MATCH'
    };
    await updateInvoice(updated, 'CONFIRMED_BOOKS_VALUE_BY_USER');
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <GitCompare className="h-6 w-6 text-cyan-400" />
            <span>3-Way GSTR-2B Reconciliation Hub</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Deterministic 3-pass cross-comparison: Purchase Books &bull; GSTR-2B &bull; Section 17(5) Blocked Credits
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#131316] px-4 py-1.5 text-xs text-slate-300">
          Target Return Period: <span className="font-bold text-white font-mono">{taxPeriod} {financialYear}</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Total Purchase ITC */}
        <div className="rounded-2xl border border-white/10 bg-[#131316] p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-slate-500">Total Purchase ITC</div>
          <div className="text-lg font-bold text-white font-mono">
            ₹{summary.totalPurchaseItc.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-slate-400">{summary.totalPurchaseInvoices} Purchase Bills in Books</div>
        </div>

        {/* Matched in 2B (Auto-Claimed) */}
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-emerald-400 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Matched in 2B (Auto-Claim)
          </div>
          <div className="text-lg font-bold text-emerald-300 font-mono">
            ₹{summary.matchedItc.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-emerald-400/80">{summary.matchedCount} Invoices 100% Eligible</div>
        </div>

        {/* Missing in 2B (At Risk) */}
        <div className="rounded-2xl border border-rose-500/30 bg-rose-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-rose-400 flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5" />
            Missing in 2B (At Risk)
          </div>
          <div className="text-lg font-bold text-rose-300 font-mono">
            ₹{summary.missingIn2bItc.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-rose-400/80">{summary.missingIn2bCount} Unfiled by Vendors</div>
        </div>

        {/* Partial Mismatches */}
        <div className="rounded-2xl border border-amber-500/30 bg-amber-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-amber-400 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            Partial Mismatches
          </div>
          <div className="text-lg font-bold text-amber-300 font-mono">
            ₹{summary.partialItc.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-amber-400/80">{summary.partialCount} Tax/Date Discrepancies</div>
        </div>

        {/* Blocked under Sec 17(5) */}
        <div className="rounded-2xl border border-violet-500/30 bg-violet-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-violet-400 flex items-center gap-1">
            <ShieldAlert className="h-3.5 w-3.5" />
            Blocked Sec 17(5)
          </div>
          <div className="text-lg font-bold text-violet-300 font-mono">
            ₹{summary.blockedItc.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-violet-400/80">{summary.blockedCount} Blocked Food/Vehicles</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-3 text-xs">
        {[
          { key: 'ALL', label: `All Bills (${results.length})` },
          { key: 'EXACT_MATCH', label: `Exact Matches (${summary.matchedCount})` },
          { key: 'PARTIAL_MATCH', label: `Partial Mismatches (${summary.partialCount})` },
          { key: 'MISSING_IN_2B', label: `Missing in 2B (${summary.missingIn2bCount})` },
          { key: 'BLOCKED_17_5', label: `Blocked Sec 17(5) (${summary.blockedCount})` }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key as any)}
            className={`px-3.5 py-1.5 rounded-xl font-medium transition ${
              activeFilter === tab.key
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Reconciliation Table */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131316]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0e1015] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Invoice #</th>
              <th className="py-3 px-4">Vendor & GSTIN</th>
              <th className="py-3 px-4 text-right">Books Tax (₹)</th>
              <th className="py-3 px-4 text-right">Portal 2B Tax (₹)</th>
              <th className="py-3 px-4 text-right">Variance</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4">Audit Diagnosis & Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300">
            {filteredResults.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-slate-500 font-sans">
                  No records matching active reconciliation filter.
                </td>
              </tr>
            ) : (
              filteredResults.map((res, idx) => {
                const recTax = res.matched2bRecord
                  ? (res.matched2bRecord.integrated_tax || 0) +
                    (res.matched2bRecord.central_tax || 0) +
                    (res.matched2bRecord.state_tax || 0) +
                    (res.matched2bRecord.cess_amount || 0)
                  : 0;

                return (
                  <tr key={idx} className="hover:bg-white/[0.03] transition">
                    <td className="py-3 px-4 font-bold text-white">
                      {res.invoice.invoice_number}
                      <div className="text-[10px] text-slate-500 font-normal">{res.invoice.invoice_date}</div>
                    </td>
                    <td className="py-3 px-4 font-sans">
                      <div className="font-semibold text-slate-200">{res.invoice.supplier_name}</div>
                      <div className="font-mono text-[10px] text-slate-400">{res.invoice.supplier_gstin || 'No GSTIN'}</div>
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-white">
                      {(res.invoice.total_tax || 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-cyan-300">
                      {res.matched2bRecord ? recTax.toFixed(2) : '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {res.taxVariance > 0 ? (
                        <span className="text-amber-400 font-bold">₹{res.taxVariance.toFixed(2)}</span>
                      ) : (
                        <span className="text-emerald-400 font-bold">₹0.00</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center font-sans">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                          res.reconStatus === 'EXACT_MATCH'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : res.reconStatus === 'PARTIAL_MATCH'
                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                        }`}
                      >
                        {res.reconStatus}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-sans text-xs">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-400 leading-snug">{res.explanation}</span>

                        {/* Action buttons */}
                        {res.reconStatus === 'MISSING_IN_2B' && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => setSelectedNudgeItem(res)}
                              className="flex items-center gap-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-2 py-1 text-[11px] font-semibold border border-emerald-500/30 transition"
                            >
                              <MessageSquare className="h-3 w-3 text-emerald-400" />
                              <span>WhatsApp Nudge</span>
                            </button>
                            <button
                              onClick={() => setSelectedNudgeItem(res)}
                              className="flex items-center gap-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-2 py-1 text-[11px] font-semibold border border-cyan-500/30 transition"
                            >
                              <Mail className="h-3 w-3 text-cyan-400" />
                              <span>Email</span>
                            </button>
                          </div>
                        )}

                        {res.reconStatus === 'PARTIAL_MATCH' && res.matched2bRecord && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => handleAcceptPortalValue(res)}
                              className="rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-2 py-1 text-[10px] font-semibold border border-cyan-500/30 transition"
                            >
                              Accept Portal (₹{recTax.toFixed(2)})
                            </button>
                            <button
                              onClick={() => handleKeepBooksValue(res)}
                              className="rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 px-2 py-1 text-[10px] font-semibold transition"
                            >
                              Keep Books (₹{(res.invoice.total_tax || 0).toFixed(2)})
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 1-Click WhatsApp & Email Follow-up Modal */}
      {selectedNudgeItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
          {(() => {
            const nudge = generateVendorNudge(
              selectedNudgeItem.invoice,
              clientLegalName,
              activeGstin
            );

            return (
              <div className="relative w-full max-w-lg rounded-2xl border border-[rgba(247,247,248,0.14)] bg-[#131316] p-6 shadow-2xl space-y-4 text-xs">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-emerald-400" />
                    <div>
                      <h3 className="font-bold text-white text-sm">Vendor ITC Follow-up Generator</h3>
                      <p className="text-[11px] text-slate-400">
                        Invoice #{selectedNudgeItem.invoice.invoice_number} &bull; ₹{selectedNudgeItem.invoice.total_tax} ITC at risk
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNudgeItem(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase text-slate-400">Email Subject</label>
                  <div className="rounded-xl border border-white/10 bg-[#0e1015] p-2.5 font-medium text-white">
                    {nudge.subject}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase text-slate-400">Pre-Formatted Message</label>
                  <div className="rounded-xl border border-white/10 bg-[#0e1015] p-3 text-slate-300 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-y-auto text-[11px]">
                    {nudge.body}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <a
                    href={nudge.whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 py-2.5 font-bold text-black transition text-center"
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span>Send on WhatsApp</span>
                  </a>
                  <a
                    href={nudge.mailToUrl}
                    className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 py-2.5 font-bold text-black transition text-center"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Open in Email App</span>
                  </a>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};
